import type { BoxProps } from "@qws-org/ui-kit";
import { Box, Button, Card, Flex, Image, Text, Title } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import type { Action, Pages, Review_Slider as ReviewSliderData } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { RatingBlock } from "~/features/review-slider/components/RatingBlock";
import { SliderPagination } from "~/features/review-slider/components/SliderPagination";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type ReviewSliderProps = {
  data: ReviewSliderData;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (control: Action) => boolean;
} & BoxProps;

export const ReviewSlider: FC<ReviewSliderProps> = ({
  getUrl,
  onCallAction,
  isControlInactive,
  data,
  ...rest
}) => {
  const { getDirectusImage } = useDirectus();
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const handleSlideChange = (action: "prev" | "next" | number): void => {
    if (!swiperRef.current) throw new Error("Swiper instance not initialized!");

    if (typeof action === "number") {
      swiperRef.current.slideToLoop(action);
    } else if (action === "prev") {
      swiperRef.current.slidePrev();
    } else if (action === "next") {
      swiperRef.current.slideNext();
    }
  };

  return (
    <Box
      width="100%"
      p={{ px: 15 }}
      {...rest}
      d={{ p: { py: 64 }, ...rest.d }}
      data-testid="review-slider"
    >
      {data?.title && (
        <Title
          fontWeight="mobile.semibold"
          as="h2"
          size="small"
          md={{
            m: { mb: 64 },
            fontSize: "title.desktop.medium",
            lineHeight: "title.desktop.medium",
          }}
          horizontalAlign="center"
          m={{ mb: 24 }}
          data-testid="review-slider-title"
        >
          {data.title}
        </Title>
      )}

      {data?.description && (
        <Text
          content={data.description}
          data-testid="review-slider-description"
        />
      )}
      <Box position={{ type: "relative" }} width={"100%"}>
        <Flex className="flex-grow" p={{ py: 24 }}>
          <Swiper
            effect="coverflow"
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            breakpoints={{
              1264: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
              340: { spaceBetween: 20, slidesPerView: "auto" },
            }}
            centeredSlides={true}
            loop={true}
            grabCursor={true}
            className="mb-6"
          >
            {data?.items?.map((item, index) => {
              const controlAction = item?.review_slider_items_id?.action;
              const action = item?.review_slider_items_id?.action?.action?.[0];
              const isInactive = controlAction
                ? isControlInactive(controlAction)
                : true;
              let href;
              if (isExternalLink(action?.item)) {
                href = action.item.href ?? undefined;
              }
              if (isInternalLink(action?.item)) {
                href = action.item.page ? getUrl(action.item.page) : undefined;
              }
              const handlePressButton = (): void => {
                if (controlAction) {
                  onCallAction(controlAction);
                }
              };
              return (
                <SwiperSlide
                  key={index}
                  className="w-full max-w-[312px] md:max-w-[400px]"
                >
                  <Card
                    variant="tertiary"
                    border={{ radius: "8px" }}
                    height="100%"
                    width="100%"
                    m={{ mb: 32 }}
                    d={{ m: { mb: 64 } }}
                  >
                    {item?.review_slider_items_id?.image?.id && (
                      <>
                        <Image
                          value={getDirectusImage(
                            item.review_slider_items_id.image.id,
                            { format: "webp", width: 164 },
                          )}
                          mdValue={getDirectusImage(
                            item.review_slider_items_id.image.id,
                            { format: "webp", width: 256 },
                          )}
                          p={{ py: 20 }}
                          md={{ p: { py: 40 } }}
                        />
                        <hr className="border border-[var(--colors-border-subtle)]  w-4/5" />
                      </>
                    )}

                    {item?.review_slider_items_id?.rating && (
                      <RatingBlock
                        rating={item.review_slider_items_id.rating}
                      />
                    )}
                    <Box width="80%">
                      <Text
                        alignment="center"
                        content={item?.review_slider_items_id?.text ?? ""}
                        m={{ mb: 24 }}
                        d={{ m: { mb: 32 } }}
                      />
                    </Box>

                    {!isInactive && (
                      <Button
                        href={href}
                        onPress={handlePressButton}
                        size="small"
                        fontWeight="mobile.semibold"
                        m={{ mt: "auto", mb: 32 }}
                        p={{ py: 12, px: 24 }}
                        d={{
                          fontWeight: "desktop.bold",
                          m: { mb: 40 },
                          p: { py: 12, px: 24 },
                        }}
                        maxWidth={240}
                        horizontalAlign="center"
                        width="max-content"
                        height="fit-content"
                        whiteSpace="pre-wrap"
                      >
                        {item?.review_slider_items_id?.action_text}
                      </Button>
                    )}
                  </Card>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Flex>{" "}
        <SliderPagination
          data={data}
          activeIndex={activeIndex}
          handleClick={handleSlideChange}
        />
      </Box>
    </Box>
  );
};
